import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../../environments/environment';

/**
 * HTTP interceptor that adds authentication tokens to requests.
 * This interceptor uses the token stored in localStorage.
 */
@Injectable()
export class ShineAuthInterceptor implements HttpInterceptor {
    private readonly tokenKey = 'shine_auth_token';

    constructor() {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip authentication for specific endpoints
        if (this.shouldSkipAuth(req.url)) {
            return next.handle(req);
        }

        if (req.headers.has('Authorization') || environment.msalEnabled) {
            return next.handle(req);
        }

        // Get token from local storage
        const token = this.getToken();
        if (!token) {
            // If no token is available, proceed without authentication
            console.warn('No authentication token available');
            return next.handle(req);
        }

        // Add token to request
        const clonedReq = req.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
        return next.handle(clonedReq);
    }

    /**
     * Gets the authentication token from local storage.
     * @returns The authentication token, or null if not available.
     */
    private getToken(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    /**
     * Determines whether authentication should be skipped for the given URL.
     * @param url The URL to check.
     * @returns True if authentication should be skipped, false otherwise.
     */
    private shouldSkipAuth(url: string): boolean {
        // Skip authentication for health check endpoints
        if (url.endsWith('/health') || url.endsWith('/health/ready')) {
            return true;
        }

        // Skip authentication for specific auth endpoints that don't require bearer tokens
        if (url.includes('/api/auth/login') || 
            url.includes('/api/auth/refresh-token') ||
            url.includes('/api/auth/validate-token')) {
            return true;
        }

        // Skip authentication for the Shine Enrich service when MSAL is enabled
        // This prevents duplicate requests with different auth tokens
        return environment.msalEnabled && url.includes(environment.enrichBaseServerUrl);


    }
}
